import {defineStore} from "pinia";
import {ApiPayload, IUser} from "@/types/api.ts";
import CommonUtils from "@/utils/CommonUtils.ts";
import {api} from "@/services/api.ts";
import {Roles} from "@/utils/constants/common.constants.ts";
import ObjectChecker from "@/utils/ObjectChecker.ts";
import {AvatarProps} from "@nuxt/ui/components/Avatar.vue";

export const useAuthStore = defineStore("auth-store", {
  state: (): { user: IUser | null, isAuthReady: boolean, apiPayload: ApiPayload } => ({
    user: null,
    isAuthReady: false,
    apiPayload: (CommonUtils.getFromStorage("apiPayload") ?? {})
  }),
  actions: {
    async initUser(user?: IUser) {
      this.isAuthReady = false;
      try {
        if (user) {
          this.user = user;
          return;
        }
        const fetchedUser = await api.auth.fetchCurrentUser();
        if (fetchedUser) {
          this.user = fetchedUser;
        } else
          this.logout()
      } catch (err) {
        this.logout()
      } finally {
        await CommonUtils.waitFor(250)
        this.isAuthReady = true
      }
    },
    async silentFetchUser() {
      try {
        const fetchedUser = await api.auth.fetchCurrentUser();
        if (fetchedUser) {
          this.user = fetchedUser;
        } else
          this.logout()
      } catch (err) {
        this.logout()
      } finally {
        await CommonUtils.waitFor(0)
      }
    },
    updateUser(updatedUser: IUser) {
      this.user = { ...(this.user || {}), ...updatedUser };
    },
    addApiPayload(apiPayload: ApiPayload) {
      this.apiPayload = {...this.apiPayload, ...apiPayload};
      CommonUtils.saveToStorage('apiPayload', this.apiPayload);
    },
    logout() {
      this.user = null;
      CommonUtils.saveToStorage('apiPayload', null);
    }
  },
  getters: {
    getPhoneNumber(): string {
      return this.apiPayload.phoneNumber ?? ''; // check if phoneNumber is not exist!!
    },
    isAdminOrSubAdmin(): boolean {
      if (!this.user)
        return false;
      return [Roles.Founder, Roles.Admin, Roles.SubAdmin].includes(this.user.role)
    },
    isAdmin(): boolean {
      if (!this.user)
        return false;
      return [Roles.Founder, Roles.Admin].includes(this.user.role)
    },
    isSubAdmin(): boolean {
      if (!this.user)
        return false;
      return [Roles.Founder, Roles.SubAdmin].includes(this.user.role)
    },
    isFounder(): boolean {
      if (!this.user)
        return false;
      return [Roles.Founder].includes(this.user.role)
    },
    isNotAdmin(): boolean {
      return !this.isAdmin
    },
    isStudent(): boolean {
      if (!this.user)
        return false;
      return [Roles.Student].includes(this.user.role);
    },
    getUserRole(): Roles {
      if (!this.user)
        return Roles.Student;
      return this.user.role;
    },
    isLoggedIn(): boolean {
      const user: IUser | null = this.user;
      return this.isAuthReady && ObjectChecker.isNotEmptyOrNull(user);
    },
    avatar(): AvatarProps {
      if (ObjectChecker.isNotEmptyOrNull(this.user?.avatar))
        return {
          src: this.user?.avatar
        };
      const firstLetter = this.user?.name?.charAt(0) ?? 'م';
      return {
        text: firstLetter
      }
    },
    getUser(): IUser {
      if (ObjectChecker.isEmptyOrNull(this.user))
        return {}
      else
        return this.user
    },
    getDisplayedName(): string {
      if (ObjectChecker.isNotEmptyOrNull(this.getUser.nickName))
        return this.getUser.nickName;
      else
        return this.getUser.name ?? '';
    }
  },
})